import { useEffect, useMemo, useState } from "react"
import { ProductYield } from "../../../state/types"
import { FieldType, FieldValue, Form, FormField, ModalId, O, Option, option, PercentField, PriceField, RadioField, StaticValue, useUIActions } from "../../../../../features/ui"
import { Product, ProductSelectField, useProductLoaders } from "../../../../products"
import { TableName } from "../../../../../tables"
import { AdjustmentType, usePricesHooks } from "../../../../operations"
import { Labels } from "../../../../../features/data-types"
import { PriceChoice } from "./types"
import { isDefined } from "../../../../../utils/typeUtils"

type Props = {
    defaultData?: Partial<ProductYield>
    baseCustomerTypeId?: string
    filterProducts?: (product: Product) => boolean
    onSubmit: (data: Partial<ProductYield>) => void
}

export const ProductYieldForm = (props: Props) => {
    const { defaultData, baseCustomerTypeId, filterProducts, onSubmit } = props
    const createMode = !defaultData

    const [products, setProducts] = useState<Product[]>([])
    const [data, setData] = useState<O<Partial<ProductYield>>>(defaultData || {})
    const [priceChoice, setPriceChoice] = useState<O<PriceChoice>>(PriceChoice.YIELD)
    const defaultAdjustmentType = defaultData?.yield && defaultData.yield > 0 ? AdjustmentType.SURCHARGE : AdjustmentType.DISCOUNT
    const [adjustmentType, setAdjustmentType] = useState<O<AdjustmentType>>(defaultAdjustmentType)
    const [price, setPrice] = useState<O<number>>(0)
    const [priceWithVat, setPriceWithVat] = useState<O<number>>(0)

    const pricesHooks = usePricesHooks()

    const productLoaders = useProductLoaders()

    const { closeModal } = useUIActions()

    const loadProducts = async () => {
      const { loadAllProducts } = productLoaders()
      const loadedProducts = await loadAllProducts()
      const activeProducts = loadedProducts.filter(product => product.id === data?.productId || !product.archived)
      return filterProducts ? activeProducts.filter(filterProducts) : loadedProducts
    }
    
    useEffect(() => {
      loadProducts().then(setProducts)
    }, [])

    useEffect(() => {
      const productId = data?.productId
      if (!productId) return

      const _yield = data?.yield
      const basePrice = pricesHooks.product.adjustedPrice(productId, false, baseCustomerTypeId)
      const basePriceVat = pricesHooks.product.adjustedPrice(productId, true, baseCustomerTypeId)

      if (priceChoice === PriceChoice.YIELD && isDefined(_yield)) {
        const newYield = Math.abs(_yield!) * (adjustmentType === AdjustmentType.DISCOUNT ? -1 : 1)
        const newPrice = pricesHooks.adjustment.apply(basePrice, newYield)
        setPrice(newPrice)
        const newPriceWithVat = pricesHooks.adjustment.apply(basePriceVat, newYield)
        setPriceWithVat(newPriceWithVat)
      } else if (priceChoice === PriceChoice.PRICE && isDefined(price)) {
        const newYield = pricesHooks.adjustment.calculateYield(price!, basePrice)
        const newAdjustmentType = newYield < 0 ? AdjustmentType.DISCOUNT : AdjustmentType.SURCHARGE
        setAdjustmentType(newAdjustmentType)
        setData({ ...data, yield: Math.abs(newYield) })
        const newPriceWithVat = pricesHooks.adjustment.apply(basePriceVat, newYield)
        setPriceWithVat(newPriceWithVat)
      } else if (priceChoice === PriceChoice.PRICE_WITH_VAT && isDefined(priceWithVat)) {
        const newYield = pricesHooks.adjustment.calculateYield(priceWithVat!, basePriceVat)
        const newAdjustmentType = newYield < 0 ? AdjustmentType.DISCOUNT : AdjustmentType.SURCHARGE
        setAdjustmentType(newAdjustmentType)
        setData({ ...data, yield: Math.abs(newYield) })
        const newPrice = pricesHooks.adjustment.apply(basePrice, newYield)
        setPrice(newPrice)
      }
    }, [data?.productId, priceChoice, adjustmentType, data?.yield, price, priceWithVat, baseCustomerTypeId])

    const priceChoiceOptions: Option[] = useMemo(() => [
      option(PriceChoice.YIELD, 'Definir ajuste (%)'),
      option(PriceChoice.PRICE, 'Definir precio de lista s/IVA'),
      option(PriceChoice.PRICE_WITH_VAT, 'Definir precio de lista c/IVA')
    ], [])

    const typeOptions = useMemo(() => Object.values(AdjustmentType).map(value => option(value, Labels.adjustmentTypeLabel(value))), [])

    const onChangeProduct = async (value?: FieldValue) => {
        const newProductId = value as O<string>
        setData({ ...data, productId: newProductId })
    }

    const onChangePriceChoice = (value?: string) => setPriceChoice(value as O<PriceChoice>)

    const onChangeAdjustmentType = (value?: FieldValue) => {
        const newType = value as O<AdjustmentType>
        setAdjustmentType(newType)
    }

    const onChangeYield = (value?: FieldValue) => {
        setData({ ...data, yield: value as O<number> })
    }

    const fields: FormField[] = [
        {
            name: 'productId',
            type: FieldType.TABLE,
            table: TableName.PRODUCTS,
            label: 'Producto',
            loading: products.length === 0,
            render: () => <ProductSelectField
                productList={products}
                defaultValue={data?.productId}
                showStock={false}
                onChange={onChangeProduct}
            />
        },
        {
          name: 'priceChoice',
          type: FieldType.SELECT,
          label: 'Definición del Precio',
          render: () => (
            <RadioField
              id="priceChoice"
              value={priceChoice}
              options={priceChoiceOptions}
              onChange={onChangePriceChoice}
            />
          )
        },
        {
            name: 'adjustmentType',
            type: FieldType.SELECT,
            value: adjustmentType,
            label: 'Tipo de Ajuste',
            options: typeOptions,
            disabled: priceChoice !== PriceChoice.YIELD,
            onChange: onChangeAdjustmentType
        },
        {
          name: 'yield',
          type: FieldType.PERCENTAGE,
          label: 'Ajuste',
          render: () => priceChoice === PriceChoice.YIELD ? (
            <PercentField
              id="yield"
              value={data?.yield && Math.abs(data.yield)}
              round={false}
              max={adjustmentType === AdjustmentType.DISCOUNT ? 100 : undefined}
              onChange={onChangeYield}
            />
          ) : (
            <StaticValue
              type={FieldType.PERCENTAGE}
              value={data?.yield}
            />
          )
        },
        {
          name: 'price',
          type: FieldType.PRICE,
          label: 'Precio s/IVA',
          render: () => priceChoice === PriceChoice.PRICE ? (
            <PriceField
              id="price"
              value={price}
              min={0.01}
              onChange={setPrice}
            />
          ) : (
            <StaticValue
              type={FieldType.PRICE}
              value={price}
            />
          )
        },
        {
          name: 'priceWithVat',
          type: FieldType.PRICE,
          label: 'Precio c/IVA',
          render: () => priceChoice === PriceChoice.PRICE_WITH_VAT ? (
            <PriceField
              id="priceWithVat"
              value={priceWithVat}
              min={0.01}
              onChange={setPriceWithVat}
            />
          ) : (
            <StaticValue
              type={FieldType.PRICE}
              value={priceWithVat}
            />
          )
        }
    ]
    
    const handleSubmit = () => {
        const _yield = Math.abs(data!.yield!)
        const newData = {
            ...data,
            yield: adjustmentType === AdjustmentType.DISCOUNT ? -_yield : _yield
        }
        onSubmit(newData)
        closeModal(ModalId.PRODUCT_YIELD_FORM)
    }

    const handleCancel = () => closeModal(ModalId.PRODUCT_YIELD_FORM)

    return (
        <Form
            formId="product-yield-form"
            fields={fields}
            createMode={createMode}
            createLabel="Agregar"
            onSubmit={handleSubmit}
            onCancel={handleCancel}
        />
    )
}
